import React, { useState } from 'react';
import {
  Box,
  VStack,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Text,
  HStack,
  Heading,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue,
  Divider,
  Switch
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { NewKBTaskForm } from './index';

/**
 * Call form component for recording call outcomes
 */
const NewKBCallForm = ({
  callStatus,
  setCallStatus,
  rescheduleTime,
  setRescheduleTime,
  callNotes,
  setCallNotes,
  lead,
  createTask,
  setCreateTask,
  taskData,
  setTaskData,
  useAiExtraction,
  onAiExtractionChange
}) => {
  // Color modes
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');

  // Get ISO string from date for datetime-local input
  const getLocalDateTimeString = (date) => {
    const d = date ? new Date(date) : new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };
  
  // Function to set preset follow-up times
  const setFollowUpPreset = (preset) => {
    const now = new Date();
    let followUpDate = new Date(now);
    
    switch(preset) {
      case '2hours':
        followUpDate.setHours(now.getHours() + 2);
        break;
      case '6hours':
        followUpDate.setHours(now.getHours() + 6);
        break;
      case '1day':
        followUpDate.setDate(now.getDate() + 1);
        break;
      case '3days':
        followUpDate.setDate(now.getDate() + 3);
        break;
      case '5days':
        followUpDate.setDate(now.getDate() + 5);
        break;
      case '7days':
        followUpDate.setDate(now.getDate() + 7);
        break;
      default:
        break;
    }
    
    setRescheduleTime(getLocalDateTimeString(followUpDate));
  };

  return (
    <Box 
      mb={6}
      p={4}
      borderWidth="1px"
      borderRadius="md"
      borderColor={borderColor}
      bg={bgColor}
    >
      <Heading size="sm" mb={4} color={textColor}>Record Call Outcome</Heading>
      
      <VStack spacing={4} align="stretch">
        {/* Call Status */}
        <FormControl isRequired>
          <FormLabel fontSize="sm">Call Status</FormLabel>
          <RadioGroup value={callStatus} onChange={setCallStatus}>
            <HStack spacing={4} wrap="wrap">
              <Radio value="yes" colorScheme="green">Answered</Radio>
              <Radio value="no" colorScheme="red">Not Answered</Radio>
              <Radio value="reschedule" colorScheme="blue">Reschedule</Radio>
            </HStack>
          </RadioGroup>
        </FormControl>
        
        {/* Reschedule Time - Show only for reschedule or answered calls */}
        {(callStatus === 'reschedule' || callStatus === 'yes') && (
          <FormControl isRequired={callStatus === 'reschedule'}>
            <FormLabel fontSize="sm">
              {callStatus === 'reschedule' ? 'Reschedule Time' : 'Follow-up Time (Optional)'}
            </FormLabel>
            <HStack spacing={2}>
              <Input
                type="datetime-local"
                value={rescheduleTime || getLocalDateTimeString(new Date())}
                onChange={(e) => setRescheduleTime(e.target.value)}
                min={getLocalDateTimeString(new Date())}
                flex="1"
              />
              <Menu>
                <MenuButton as={Button} rightIcon={<ChevronDownIcon />} size="md">
                  Preset
                </MenuButton>
                <MenuList>
                  <MenuItem onClick={() => setFollowUpPreset('2hours')}>In 2 hours</MenuItem>
                  <MenuItem onClick={() => setFollowUpPreset('6hours')}>In 6 hours</MenuItem>
                  <MenuItem onClick={() => setFollowUpPreset('1day')}>Tomorrow</MenuItem>
                  <MenuItem onClick={() => setFollowUpPreset('3days')}>In 3 days</MenuItem>
                  <MenuItem onClick={() => setFollowUpPreset('5days')}>In 5 days</MenuItem>
                  <MenuItem onClick={() => setFollowUpPreset('7days')}>In 1 week</MenuItem>
                </MenuList>
              </Menu>
            </HStack>
            <Text fontSize="xs" color="gray.500" mt={1}>
              Select a date and time for the {callStatus === 'reschedule' ? 'rescheduled call' : 'next follow-up'} or use a preset
            </Text>
          </FormControl>
        )}
        
        {/* Task Creation Form */}
        <NewKBTaskForm
          createTask={createTask}
          setCreateTask={setCreateTask}
          taskData={taskData}
          setTaskData={setTaskData}
          lead={lead}
        />
        
        {/* Call Notes */}
        <FormControl>
          <FormLabel fontSize="sm">Call Notes</FormLabel>
          <Textarea
            value={callNotes}
            onChange={(e) => setCallNotes(e.target.value)}
            placeholder="Enter details of the call..."
            rows={4}
          />
        </FormControl>
        
        {/* AI Extraction */}
        <FormControl display="flex" alignItems="center" mt={2}>
          <Switch 
            id="use-ai-extraction" 
            size="sm" 
            colorScheme="blue"
            isChecked={useAiExtraction}
            onChange={(e) => onAiExtractionChange?.(e.target.checked)}
          />
          <FormLabel htmlFor="use-ai-extraction" mb="0" ml={2} fontSize="sm">
            Use AI to extract lead information
          </FormLabel>
        </FormControl>
      </VStack>
    </Box>
  );
};

export default NewKBCallForm;
